import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartPricing } from '../contexts/CartPricingContext';
import { ApplePayOption, buildApplePayRecurringOption, hardwareFirstChargeForSheet } from '../lib/applePayRecurring';
import useHardwareFirstTrialRecurringCharge from './useHardwareFirstTrialRecurringCharge';
import useHardwareFirstTrialTerms from './useHardwareFirstTrialTerms';
import useHardwareFirstTrialUnits from './useHardwareFirstTrialUnits';

/**
 * The apple pay sheet options for the cart being checked out, shared by the payment accordion and
 * the express row. `applePayRecurring` requests an Apple Pay merchant token (MPAN) so the card can
 * be charged off-session for membership renewals; undefined when the cart has no recurring charge.
 * `trialPending` is a trial cart whose terms are not priced yet: it must not offer apple pay, since
 * the sheet would open without the tracker's day-30 charge.
 */
export default function useHardwareFirstApplePayOption(): {
  applePayRecurring: ApplePayOption | undefined;
  trialPending: boolean;
} {
  const { t } = useTranslation();
  const { totalRecurringCharges, hardwareFirstRecurringCharge } = useCartPricing();
  const trialCharge = useHardwareFirstTrialRecurringCharge();
  const trialUnits = useHardwareFirstTrialUnits();
  const trialListPrice = useHardwareFirstTrialTerms(trialUnits.listPriceInCents || undefined)?.listPrice;
  const trialActive = !!trialCharge && !!trialListPrice;
  const trialPending = trialUnits.count > 0 && !trialActive;
  // one object per pricing result: the express element only takes `applePay` at creation, and a
  // fresh object every render would push it through update() on each one
  const applePayRecurring = useMemo(() => {
    const hardwareDisclosure = trialActive
      ? { paymentDescription: t('apple_pay_fi_trial_tracker_then_membership', { listPrice: trialListPrice }) }
      : hardwareFirstRecurringCharge
        ? { paymentDescription: t('apple_pay_fi_tracker_and_membership') }
        : undefined;
    return buildApplePayRecurringOption(
      totalRecurringCharges,
      hardwareFirstChargeForSheet(trialActive, trialCharge, hardwareFirstRecurringCharge),
      hardwareDisclosure,
    );
  }, [t, trialActive, trialListPrice, totalRecurringCharges, trialCharge, hardwareFirstRecurringCharge]);

  return { applePayRecurring, trialPending };
}
